// Segmented control for switching which metric ReportsOverUnderChart.js
// shows (kCal/Protein/Carbs/Fat) — extracted out of that file since it's
// the one sub-piece with no coupling to the chart's own tooltip/bar-shape
// internals, unlike those two (kept self-contained enough to reuse if a
// future chart ever needs the same 4-metric toggle).

import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { translateMacroLabel } from "./viewTokens";
import { OVER_UNDER_METRICS } from "../../../../../utils/nutrition-progress";

const mt = (t, key, opts) => t(`nutritionPage.progress.reports.overUnderChart.${key}`, opts);

// Standard tablist keyboard pattern: ArrowLeft/Right both moves focus AND
// activates the newly-focused metric (single-select "automatic activation",
// same behavior a real <select> gives you) — roving tabIndex (only the
// active tab is in the Tab order) keeps this consistent with how the rest
// of the page tabs through controls.
export const MetricToggle = ({ activeMetric, onChange }) => {
  const { t } = useTranslation("athlete-portal");
  const btnRefs = useRef({});

  const handleKeyDown = (e, idx) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const nextMetric = OVER_UNDER_METRICS[(idx + dir + OVER_UNDER_METRICS.length) % OVER_UNDER_METRICS.length];
    onChange(nextMetric);
    btnRefs.current[nextMetric]?.focus();
  };

  return (
    <div className="np-overunder-chart__toggle" role="tablist" aria-label={mt(t, "metricToggleLabel")}>
      {OVER_UNDER_METRICS.map((metric, idx) => {
        const selected = metric === activeMetric;
        return (
          <button
            key={metric}
            ref={(el) => {
              btnRefs.current[metric] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`np-overunder-chart__toggle-btn${selected ? " is-active" : ""}`}
            onClick={() => onChange(metric)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
          >
            {translateMacroLabel(t, metric)}
          </button>
        );
      })}
    </div>
  );
};

MetricToggle.propTypes = {
  activeMetric: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default MetricToggle;
